import { useEffect, useMemo, useState, VFC } from 'react'
import { createMyPageActionLog } from 'src/api/mypage'
import { postInterested } from 'src/api/recommendation'
import {
  CONTRIBUTION_ANONYMOUS_MOSAIC,
  CONTRIBUTION_USER,
  DEFAULT_IMAGE,
  TIMELINE_BATCH_MESSAGE,
  CONTRIBUTION_USER as USER_ICON,
} from 'src/assets/images'
import {
  PositionIcon,
  TimelinePremiumContributionCalendarIcon,
  TimelinePremiumContributionPriceIcon,
  TimelinePremiumContributionPurposeIcon,
  TimelinePremiumContributionUserIcon,
  TypeOfBusinessIcon,
} from 'src/assets/svgs'
import { FeedbackScore } from 'src/components/Rating/FeedbackScore'
import { Link } from 'src/elements/Link'
import { PremiumContribution } from 'src/models/PremiumContribution'
import { TimelineDataType, TimelineType } from 'src/models/Timeline'
import { LoginUser } from 'src/models/User'
import {
  _menuicon,
  _message_tag_label,
  _timelineBatchMessage,
  _timelineContribution,
  _timelineContributionTitle,
  _timelineDate,
  _timelineEvent,
  _timelineEventTitle,
  _timelineInfo,
  _timelineMessage,
  _timelineMessageText,
  _timelineNewUser,
  _timelineNewUserTitle,
  _timelinePremiumContribution,
  _timelinePremiumContributionTitle,
  _timelineRecommendedUser,
  _timelineRecommendedUserTitle,
  _timelineRequestedUser,
  _timelineRequestedUserTitle,
  _timelineRequestReason,
  safetyOrange,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { WIDTH_THRESHOLD } from 'src/styles/mixins'
import {
  companyDetail,
  contributionDetail,
  eventDetail,
} from 'src/utils/routes'
import styled, { css } from 'styled-components'
import {
  getTypeOfPurchaseText,
  SubInformation,
} from '../Contributions/ContributionCard'
import { PremiumContributionModal } from '../Contributions/PremiumContributionModal'

type currentListType = 'all' | TimelineDataType
type buttonType = { label: string; type: currentListType }

const buttons: buttonType[] = [
  { label: '全て', type: 'all' },
  { label: '新着メッセージ', type: 'room' },
  { label: 'つながり申請', type: 'requestedUser' },
  { label: 'AIピック', type: 'recommendedUser' },
  { label: '案件を探す', type: 'premiumContribution' },
  { label: '掲示板投稿', type: 'contribution' },
  { label: 'イベント開催', type: 'event' },
  { label: '新着企業', type: 'newUser' },
]
const CONTENT_LIMIT = 50

type TimelineProps = {
  timeline: TimelineType[]
  hasPaid: boolean
  mutateTimeline: () => void
  user: LoginUser
}
export const Timeline: VFC<TimelineProps> = ({ timeline, hasPaid, user }) => {
  const [currentList, setCurrentList] = useState<currentListType>('all')
  const [premiumContributionForModal, setPremiumContributionForModal] =
    useState<PremiumContribution | undefined>(undefined)

  const onSubmitPostInterested = (
    companyId: number,
    reason: string,
    detail: string,
    premiumContributionId: number,
  ) => {
    postInterested(
      companyId,
      reason,
      undefined,
      detail,
      premiumContributionId,
    ).then(() => {
      setPremiumContributionForModal(undefined)
    })
  }

  const timelineList = useMemo(() => {
    return timeline.filter(
      (each) =>
        (currentList === 'all' || each.type === currentList) &&
        (each.type !== 'newUser' || user.plan.paid),
    )
  }, [timeline, currentList, user])

  const filteredButtons = useMemo(() => {
    return buttons.filter((each) => each.type !== 'newUser' || user.plan.paid)
  }, [user])

  const onClickCreateLog = async (params: {
    item: string
    targetCompanyId?: number
    targetObjectId?: number
    memo?: string
  }) => {
    createMyPageActionLog({
      component: 'Timeline',
      item: params.item,
      targetCompanyId: params.targetCompanyId,
      targetObjectId: params.targetObjectId,
      memo: params.memo,
    })
  }

  const [width, setWidth] = useState(window.innerWidth)
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  const linkText1 = width < WIDTH_THRESHOLD ? '＞' : '確認/返信する ＞'
  const linkText2 = width < WIDTH_THRESHOLD ? '＞' : '詳細を見る ＞'

  return (
    <>
      <TimelineLayout>
        <DisplayFlex>
          <SideMenu>
            {filteredButtons.map((each, i) => (
              <SideMenuButton
                key={i}
                onClick={() => setCurrentList(each.type)}
                style={{
                  backgroundColor: each.type === currentList ? 'white' : '',
                }}
              >
                {each.label}
              </SideMenuButton>
            ))}
          </SideMenu>

          <TimelineList>
            {timelineList.length === 0 && (
              <TimelineListBlank>
                {
                  buttons.filter((button) => button.type === currentList)[0]
                    .label
                }
                はありません
              </TimelineListBlank>
            )}
            {timelineList.map((each, i) => {
              switch (each.type) {
                case 'room':
                  const room = each.value
                  const unreadBatchMessageRoom = room.unreadBatchMessageRoom
                  return (
                    <ItemBase key={i}>
                      <ItemCardTitle
                        heading1={
                          unreadBatchMessageRoom
                            ? '運営からのお知らせ'
                            : '新着メッセージ'
                        }
                        heading2={each.date}
                        color={
                          unreadBatchMessageRoom
                            ? truewhite
                            : _timelineMessageText
                        }
                        backGroundColor={
                          unreadBatchMessageRoom
                            ? _timelineBatchMessage
                            : _timelineMessage
                        }
                        dateColor={
                          unreadBatchMessageRoom ? truewhite : _timelineDate
                        }
                      />
                      <ItemContent>
                        <DisplayFlex>
                          {room.isGroup ? (
                            <ItemContentImageDiv>
                              <MultiUserIcon>
                                {room.counters.map((counter) => (
                                  <ItemContentGroupImage
                                    key={counter.id}
                                    src={counter.iconSrc || DEFAULT_IMAGE}
                                  />
                                ))}
                              </MultiUserIcon>
                            </ItemContentImageDiv>
                          ) : (
                            <ImageComponent
                              src={
                                room.unreadBatchMessageRoom
                                  ? TIMELINE_BATCH_MESSAGE
                                  : room.counters[0].iconSrc
                              }
                              rounded={!room.unreadBatchMessageRoom}
                            />
                          )}
                          <FlexColumn>
                            <ItemContentMessage>
                              {room.latestMessage?.content &&
                                (room.latestMessage.content.length >
                                CONTENT_LIMIT ? (
                                  <>
                                    {room.latestMessage.content.slice(
                                      0,
                                      CONTENT_LIMIT,
                                    )}
                                    …
                                  </>
                                ) : (
                                  room.latestMessage.content
                                ))}
                            </ItemContentMessage>
                            {room.isGroup ? (
                              <DisplayFlex>
                                <img
                                  src={USER_ICON}
                                  height={24}
                                  width={24}
                                  alt=""
                                />
                                <ItemInfoRow>{`${room.counters[0].companyName} / ${room.counters[1].companyName}`}</ItemInfoRow>
                              </DisplayFlex>
                            ) : (
                              <ItemInfo
                                text1={`${room.counters[0].name}(${room.counters[0].companyName})`}
                                text2={room.counters[0].position || ''}
                              />
                            )}
                            <FeedbackScore
                              value={room.counters[0].feedbackScore}
                              feedbackCount={room.counters[0].feedbackScore}
                            />
                          </FlexColumn>
                        </DisplayFlex>
                        <ItemContentReplyOrange
                          onClick={() =>
                            onClickCreateLog({
                              item: each.type,
                              targetObjectId: room.id,
                              memo: room.type,
                            })
                          }
                          href={room.urlForTimeline}
                          color={
                            unreadBatchMessageRoom ? _timelineBatchMessage : ''
                          }
                        >
                          {linkText1}
                        </ItemContentReplyOrange>
                      </ItemContent>
                    </ItemBase>
                  )
                case 'requestedUser':
                  const requestedUser = each.value
                  return (
                    <ItemBase key={i}>
                      <ItemCardTitle
                        heading1={'つながり申請'}
                        heading2={each.date}
                        color={_timelineRequestedUserTitle}
                        backGroundColor={_timelineRequestedUser}
                      />
                      <ItemContent>
                        <DisplayFlex>
                          <ImageComponent src={requestedUser.imageSrc} />
                          <FlexColumn>
                            <ItemRequestReason>
                              {requestedUser.requestReasonText}
                            </ItemRequestReason>
                            <ItemContentMessage>
                              <p>{`${requestedUser.companyName}の${requestedUser.name}さんからつながり申請が届いております`}</p>
                            </ItemContentMessage>
                            <ItemInfo
                              text1={`${requestedUser.name}(${requestedUser.companyName})`}
                              text2={requestedUser.position}
                            />
                            <FeedbackScore
                              value={requestedUser.feedbackScore}
                              feedbackCount={requestedUser.feedbackCount}
                            />
                          </FlexColumn>
                        </DisplayFlex>
                        <ItemContentReplyOrange
                          href={requestedUser.roomUrl}
                          onClick={() =>
                            onClickCreateLog({
                              item: each.type,
                              targetCompanyId: requestedUser.id,
                            })
                          }
                        >
                          {linkText1}
                        </ItemContentReplyOrange>
                      </ItemContent>
                    </ItemBase>
                  )
                case 'recommendedUser':
                  const recommendedUser = each.value
                  return (
                    <ItemBase key={i}>
                      <ItemCardTitle
                        heading1={'AIピック'}
                        heading2={each.date}
                        color={_timelineRecommendedUserTitle}
                        backGroundColor={_timelineRecommendedUser}
                      />
                      <ItemContent>
                        <DisplayFlex>
                          <ImageComponent src={recommendedUser.imageSrc} />
                          <FlexColumn>
                            <ItemContentMessage>
                              <p>{recommendedUser.name}</p>
                              <MarginBottom8 />
                              <p>{recommendedUser.companyName}</p>
                            </ItemContentMessage>
                            <DisplayFlexWrap>
                              <DisplayFlex>
                                <PositionIcon />
                                <ItemInfoRow>
                                  {recommendedUser.position}
                                </ItemInfoRow>
                              </DisplayFlex>
                              <DisplayFlex>
                                <TypeOfBusinessIcon />
                                <ItemInfoRow>
                                  {Array.isArray(recommendedUser.typeOfBusiness)
                                    ? recommendedUser.typeOfBusiness.join('、')
                                    : recommendedUser.typeOfBusiness || ''}
                                </ItemInfoRow>
                              </DisplayFlex>
                              <DisplayFlex>
                                {recommendedUser.prefecture && (
                                  <ItemInfoRow>{`所在地：${recommendedUser.prefecture}`}</ItemInfoRow>
                                )}
                              </DisplayFlex>
                              <DisplayFlex>
                                {recommendedUser.foundedOn && (
                                  <ItemInfoRow>
                                    設立年数：{' '}
                                    {new Date().getFullYear() -
                                      +recommendedUser.foundedOn}
                                    年
                                  </ItemInfoRow>
                                )}
                              </DisplayFlex>
                            </DisplayFlexWrap>
                            <FeedbackScore
                              value={recommendedUser.feedbackScore}
                              feedbackCount={recommendedUser.feedbackCount}
                            />
                          </FlexColumn>
                        </DisplayFlex>
                        <ItemContentReplyAnchor
                          onClick={() => {
                            window.open(
                              companyDetail(recommendedUser.id, false, true),
                              '_blank',
                            )
                            onClickCreateLog({
                              item: each.type,
                              targetCompanyId: recommendedUser.id,
                            })
                          }}
                        >
                          {linkText2}
                        </ItemContentReplyAnchor>
                      </ItemContent>
                    </ItemBase>
                  )
                case 'premiumContribution':
                  const premiumContribution = each.value
                  return (
                    <ItemBase key={i}>
                      <ItemCardTitle
                        heading1={'案件を探す'}
                        heading2={each.date}
                        color={_timelinePremiumContributionTitle}
                        backGroundColor={_timelinePremiumContribution}
                      />
                      <ItemContent>
                        <FlexColumn>
                          <ItemContentMessage>
                            {premiumContribution.title}
                          </ItemContentMessage>
                          <DisplayFlexWrap>
                            <DisplayFlex>
                              <TimelinePremiumContributionPurposeIcon />
                              <ItemInfoRow>
                                {getTypeOfPurchaseText(
                                  premiumContribution.purpose,
                                )}
                              </ItemInfoRow>
                            </DisplayFlex>
                            <DisplayFlex>
                              <img
                                src={CONTRIBUTION_USER}
                                height={24}
                                width={24}
                                alt=""
                              />
                              <img
                                src={CONTRIBUTION_ANONYMOUS_MOSAIC}
                                height={24}
                                alt=""
                              />
                            </DisplayFlex>
                            <DisplayFlex>
                              <TimelinePremiumContributionCalendarIcon />
                              <ItemInfoRow>
                                {premiumContribution.endAt.format('YYYY/MM/DD')}
                                まで募集中
                              </ItemInfoRow>
                            </DisplayFlex>
                          </DisplayFlexWrap>
                          <DisplayFlexWrap>
                            <DisplayFlex>
                              <TimelinePremiumContributionPriceIcon />
                              <ItemInfoRow>
                                予算：{premiumContribution.displayBudgetText}
                              </ItemInfoRow>
                            </DisplayFlex>
                            <DisplayFlex>
                              <TimelinePremiumContributionPriceIcon />
                              <ItemInfoRow>
                                売上規模：{premiumContribution.salesScaleText}
                              </ItemInfoRow>
                            </DisplayFlex>
                            <DisplayFlex>
                              <TimelinePremiumContributionUserIcon />
                              <ItemInfoRow>
                                従業員数：
                                {premiumContribution.employeeNumberText}
                                以上
                              </ItemInfoRow>
                            </DisplayFlex>
                          </DisplayFlexWrap>
                          <MarginBottom8 />
                          <PreWrap>
                            {premiumContribution.content &&
                              (premiumContribution.content.length >
                              CONTENT_LIMIT ? (
                                <>
                                  {premiumContribution.content.slice(
                                    0,
                                    CONTENT_LIMIT,
                                  )}
                                  …
                                </>
                              ) : (
                                premiumContribution.content
                              ))}
                          </PreWrap>
                        </FlexColumn>
                        {hasPaid && (
                          <ItemContentReplyAnchor
                            onClick={() => {
                              setPremiumContributionForModal(
                                premiumContribution,
                              )
                              onClickCreateLog({
                                item: each.type,
                                targetObjectId: premiumContribution.id,
                              })
                            }}
                          >
                            {linkText2}
                          </ItemContentReplyAnchor>
                        )}
                      </ItemContent>
                    </ItemBase>
                  )
                case 'contribution':
                  const contribution = each.value
                  // モザイクを外す（「導入したい」「相談したい」のモザイクを外す）
                  const isMask = false
                  return (
                    <ItemBase key={i}>
                      <ItemCardTitle
                        heading1={'掲示板投稿'}
                        heading2={each.date}
                        color={_timelineContributionTitle}
                        backGroundColor={_timelineContribution}
                      />
                      <ItemContent>
                        <DisplayFlex>
                          <ImageComponent src={contribution.iconSrc} />
                          <FlexColumn>
                            <ItemContentMessage>
                              <PreWrap
                                style={{
                                  filter: isMask ? 'blur(4px)' : 'none',
                                }}
                              >
                                {contribution.background &&
                                  (contribution.background.length >
                                  CONTENT_LIMIT ? (
                                    <>
                                      {contribution.background.slice(
                                        0,
                                        CONTENT_LIMIT,
                                      )}
                                      …
                                    </>
                                  ) : (
                                    contribution.background
                                  ))}
                              </PreWrap>
                            </ItemContentMessage>
                            <SubInformation
                              typeOfPurchase={contribution.typeOfPurchase}
                              publishedAt={contribution.publishedAt}
                              name={contribution.name}
                              companyName={contribution.companyName}
                              style={{
                                fontSize: 14,
                                color: _timelineInfo,
                                flexWrap: 'wrap',
                              }}
                              isTimeline
                              isMask={isMask}
                            />
                            <FeedbackScore
                              value={contribution.feedbackScore}
                              feedbackCount={contribution.feedbackCount}
                            />
                          </FlexColumn>
                        </DisplayFlex>
                        <ItemContentReply
                          href={contributionDetail(contribution.id)}
                          onClick={() => {
                            onClickCreateLog({
                              item: each.type,
                              targetObjectId: contribution.id,
                            })
                          }}
                        >
                          {linkText2}
                        </ItemContentReply>
                      </ItemContent>
                    </ItemBase>
                  )
                case 'event':
                  const event = each.value
                  return (
                    <ItemBase key={i}>
                      <ItemCardTitle
                        heading1={'イベント開催'}
                        heading2={each.date}
                        color={_timelineEventTitle}
                        backGroundColor={_timelineEvent}
                      />
                      <ItemContent>
                        <DisplayFlex>
                          <FlexColumn>
                            <ItemContentMessage>
                              <p>{event.name}</p>
                            </ItemContentMessage>
                            <MarginBottom8 />
                            <p>
                              開催日：
                              {event.openingAt.format('YYYY/MM/DD HH:mm')}〜
                            </p>
                            <MarginBottom8 />
                            <EventImage src={event.imageFile.url} />
                          </FlexColumn>
                        </DisplayFlex>
                        <ItemContentReply
                          href={eventDetail(event.id)}
                          onClick={() => {
                            onClickCreateLog({
                              item: each.type,
                              targetObjectId: event.id,
                            })
                          }}
                        >
                          {linkText2}
                        </ItemContentReply>
                      </ItemContent>
                    </ItemBase>
                  )
                case 'newUser':
                  const newUser = each.value
                  return (
                    <ItemBase key={i}>
                      <ItemCardTitle
                        heading1={'新着企業'}
                        heading2={each.date}
                        color={_timelineNewUserTitle}
                        backGroundColor={_timelineNewUser}
                      />
                      <ItemContent>
                        <DisplayFlex>
                          <ImageComponent src={newUser.imageSrc} />
                          <FlexColumn>
                            <ItemContentMessage>
                              <p>{newUser.name}</p>
                              <MarginBottom8 />
                              <p>{newUser.companyName}</p>
                            </ItemContentMessage>
                            <DisplayFlexWrap>
                              <DisplayFlex>
                                <PositionIcon />
                                <ItemInfoRow>{newUser.position}</ItemInfoRow>
                              </DisplayFlex>
                              <DisplayFlex>
                                <TypeOfBusinessIcon />
                                <ItemInfoRow>
                                  {Array.isArray(newUser.typeOfBusiness)
                                    ? newUser.typeOfBusiness.join('、')
                                    : newUser.typeOfBusiness || ''}
                                </ItemInfoRow>
                              </DisplayFlex>
                            </DisplayFlexWrap>
                          </FlexColumn>
                        </DisplayFlex>
                        <ItemContentReplyAnchor
                          onClick={() => {
                            window.open(
                              companyDetail(newUser.id, true, false),
                              '_blank',
                            )
                            onClickCreateLog({
                              item: each.type,
                              targetCompanyId: newUser.id,
                            })
                          }}
                        >
                          {linkText2}
                        </ItemContentReplyAnchor>
                      </ItemContent>
                    </ItemBase>
                  )
                default:
                  return <></>
              }
            })}
          </TimelineList>
        </DisplayFlex>
      </TimelineLayout>
      {premiumContributionForModal && (
        <PremiumContributionModal
          premiumContribution={premiumContributionForModal}
          close={() => setPremiumContributionForModal(undefined)}
          onSubmitPostInterested={onSubmitPostInterested}
          hasPaid={hasPaid}
        />
      )}
    </>
  )
}

const ItemCardTitle: VFC<{
  heading1: string
  heading2: string
  color: string
  backGroundColor: string
  dateColor?: string
}> = ({ heading1, heading2, color, backGroundColor, dateColor }) => (
  <ItemTitleDiv backGroundColor={backGroundColor}>
    <ItemTitle color={color}>{heading1}</ItemTitle>
    <ItemTitleDate dateColor={dateColor} color={color}>
      {heading2}
    </ItemTitleDate>
  </ItemTitleDiv>
)

const ImageComponent: VFC<{
  src: string | undefined | null
  rounded?: boolean
}> = ({ src, rounded = true }) => (
  <ItemContentImageDiv>
    <ItemContentImage
      src={src || DEFAULT_IMAGE}
      rounded={rounded}
      onError={(e) => {
        const img = e.currentTarget
        if (img.src !== DEFAULT_IMAGE) img.src = DEFAULT_IMAGE
      }}
    />
  </ItemContentImageDiv>
)

const ItemInfo: VFC<{
  text1: string
  text2: string
}> = ({ text1, text2 }) => (
  <DisplayFlexWrap>
    <DisplayFlex>
      <img src={USER_ICON} height={24} width={24} alt="" />
      <ItemInfoRow>{text1}</ItemInfoRow>
    </DisplayFlex>
    <DisplayFlex>
      <PositionIcon />
      <ItemInfoRow>{text2}</ItemInfoRow>
    </DisplayFlex>
  </DisplayFlexWrap>
)

const TimelineLayout = styled.div`
  max-width: 1000px;
  width: 100%;
  margin: 40px auto;
  padding-top: 40px;
  border-top: 1px solid ${_menuicon};
`

const SideMenu = styled.div`
  min-width: 230px;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-weight: ${fontWeightBold};
  font-size: 16px;
  button {
    margin-bottom: 4px;
    padding-left: 20px;
    height: 55px;
  }
  margin-right: 16px;
  @media screen and (max-width: 960px) {
    display: none;
  }
`

const TimelineList = styled.div`
  width: 100%;
`

const TimelineListBlank = styled.div`
  padding-top: 80px;
  text-align: center;
  font-size: 16px;
  font-weight: ${fontWeightBold};
`

const SideMenuButton = styled.button`
  border-radius: 8px;
`

const ItemBase = styled.div`
  width: 100%;
  padding: 0 8px 16px;
`

const ItemTitleDiv = styled.div<{ backGroundColor: string }>`
  display: flex;
  justify-content: space-between;
  padding: 8px 20px;
  height: 36px;
  align-items: center;
  border-radius: 8px 8px 0px 0px;
  ${({ backGroundColor }) =>
    css`
      background-color: ${backGroundColor};
    `}
`

const ItemTitle = styled.p<{ color: string }>`
  ${({ color }) =>
    css`
      color: ${color};
    `}
  font-weight: ${fontWeightBold};
`

const ItemTitleDate = styled.p<{ dateColor?: string }>`
  ${({ dateColor }) =>
    dateColor
      ? css`
          color: ${dateColor};
        `
      : css`
          color: ${_timelineDate};
        `}
`

const ItemContent = styled.div`
  border-top: 1px solid ${_message_tag_label};
  padding: 20px;
  background-color: ${truewhite};
  border-radius: 0px 0px 8px 8px;
  display: flex;
  justify-content: space-between;
`

const DisplayFlex = styled.div`
  display: flex;
`

const DisplayFlexWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const ItemContentImageDiv = styled.div`
  margin: auto 0;
  padding-right: 12px;
`

const ItemContentImage = styled.img<{ rounded?: boolean }>`
  ${({ rounded }) => rounded && 'border-radius: 50%;'}
  object-fit: cover;
  width: 80px;
  height: 80px;
`

const ItemContentGroupImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
`

const MultiUserIcon = styled.div`
  position: relative;
  width: 80px;
  height: 80px;

  > * {
    :nth-child(1) {
      position: absolute;
      top: 0px;
      right: 0;
    }
    :nth-child(2) {
      position: absolute;
      bottom: 0px;
      left: 0;
    }
  }
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`
const ItemContentCss = css`
  width: 100%;
  font-size: 18px;
  font-weight: ${fontWeightBold};
  white-space: pre-wrap;
  padding: 8px 0;
`
const ItemContentMessage = styled.div`
  ${ItemContentCss}
  max-width: 500px;
`
const ItemInfoRow = styled.p`
  margin: auto 0;
  padding-left: 8px;
  margin-right: 12px;
`

const ItemContentReplyBase = css`
  font-size: 20px;
  font-weight: ${fontWeightBold};
  text-align: right;
  padding-left: 12px;
  margin: auto 0;
  @media screen and (min-width: 960px) {
    min-width: 170px;
  }
`

const ItemContentReplyOrange = styled(Link)<{ color?: string }>`
  ${ItemContentReplyBase}
  width: 180px;

  ${({ color }) =>
    color
      ? css`
          color: ${color};
        `
      : css`
          color: ${safetyOrange};
        `}
`

const ItemContentReply = styled(Link)`
  ${ItemContentReplyBase}
`

const ItemContentReplyAnchor = styled.a`
  ${ItemContentReplyBase}
`

const ItemRequestReason = styled.p`
  display: inline-block;
  border: 1px solid ${_timelineRequestReason};
  color: ${_timelineRequestReason};
  padding: 4px 16px;
  border-radius: 20px;
`

const MarginBottom8 = styled.div`
  margin-bottom: 8px;
`

const PreWrap = styled.p`
  white-space: pre-wrap;
`

const EventImage = styled.img`
  max-width: 524px;
  width: 100%;
  max-height: 275px;
  height: 100%;
  border-radius: 8px 0px 0px 0px;
  object-fit: contain;
`
